import React, { useContext, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import RichContentViewer from "../../../Shared/RichContentViewer";
import { getActionsInRealTime } from "../../../Config/db/actions.retrieve";
import {
  updateDailyReport,
  deleteDailyReport,
} from "../../../Config/db/daily-report.retrieve";
import Actions from "../Actions/Actions";
import { Assessment, MoreVert, Edit, Delete } from "@mui/icons-material";
import {
  Box, Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Menu, MenuItem,
  Stack, TextField,
} from "@mui/material";
import moment from "moment/moment";
import MUIRichTextEditor from "mui-rte";
import { ThemeProvider } from "@mui/material/styles";
import { convertToRaw, ContentState } from "draft-js";
import { editorTheme } from "../../../Shared/RHFRichEditor";
import { isDraftJsContent } from "../helper";

export default function WorkLogEntry({ report: { report, id, timestamp, editedAt } }) {
  const [actions, setActions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(report || "");
  const [saving, setSaving] = useState(false);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const unsub = getActionsInRealTime(id, (data) => setActions(data));
    return () => unsub && unsub();
  }, [id]);

  const reportDate = timestamp?.seconds
    ? moment.unix(timestamp.seconds).format("MMM D, YYYY h:mm A")
    : "";

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await updateDailyReport(id, editText);
      setEditing(false);
    } catch (err) {
      console.error("Failed to update work log:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDailyReport(id);
    } catch (err) {
      console.error("Failed to delete work log:", err);
    }
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          "&:hover": {
            borderColor: "#1b5e20",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          },
          transition: "all 0.2s",
        }}
      >
        <CardContent sx={{ pb: actions.length > 0 ? 1 : undefined }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Assessment sx={{ color: "#1b5e20", fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Work Log
              </Typography>
              {editedAt && (
                <Typography variant="caption" color="text.disabled">
                  (edited)
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {reportDate && (
                <Typography variant="caption" color="text.secondary">
                  {reportDate}
                </Typography>
              )}
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {editing ? (
            <Box>
              <Box sx={{ mb: 1.5 }}>
                <ThemeProvider theme={editorTheme}>
                  <MUIRichTextEditor
                    label="Edit notes..."
                    defaultValue={
                      isDraftJsContent(report)
                        ? report
                        : report
                          ? JSON.stringify(convertToRaw(ContentState.createFromText(report)))
                          : undefined
                    }
                    onChange={(editorState) => {
                      const raw = convertToRaw(editorState.getCurrentContent());
                      setEditText(JSON.stringify(raw));
                    }}
                    controls={[
                      "bold", "italic", "underline", "strikethrough",
                      "highlight", "link", "numberList", "bulletList",
                      "quote", "code",
                    ]}
                    inlineToolbar={true}
                  />
                </ThemeProvider>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setEditing(false); setEditText(report || ""); }}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving}
                  onClick={handleEditSave}
                  sx={{ textTransform: "none", borderRadius: 2, background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)" }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Stack>
            </Box>
          ) : (
            <RichContentViewer content={report || "No notes"} />
          )}
        </CardContent>

        {actions.length > 0 && (
          <CardContent
            sx={{ pt: 0, bgcolor: "#fafafa", borderTop: "1px solid #f0f0f0" }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Follow-up Tasks
            </Typography>
            <Actions actions={actions} />
          </CardContent>
        )}
      </Card>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setEditing(true);
            setEditText(report || "");
            setMenuAnchor(null);
          }}
          dense
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setMenuAnchor(null);
          }}
          dense
          sx={{ color: "error.main" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Work Log</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this work log? Associated follow-up tasks will remain but will no longer be linked.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: "none" }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
